// ============================================================================
// Client-contact picker options — consumed by BidDetailPage.
// ----------------------------------------------------------------------------
// `clients` is ONE ROW PER CONTACT PERSON, not per company: several rows
// routinely share a company (four share 'EEE' in the dev data, three share
// 'SEPT'). A bid names the contact so conversion can file the project against a
// specific person; a company name alone cannot identify one.
//
// Any place that lets you set a bid's client builds its options here, so two
// surfaces can never drift into labelling the same row differently.
// ============================================================================

import type { Client } from '@frontend/Models/Client/ClientModels';
import type { SelectOption } from '@frontend/Models/Form/FormModels';

const person = (c: Client): string =>
  [c.FirstName, c.LastName].filter(Boolean).join(' ').trim();

const company = (c: Client): string => (c.Company ?? '').trim();

/** Company first: you pick the organisation you are bidding to, then the person within it. */
function baseLabel(c: Client): string {
  const co = company(c);
  const pe = person(c);
  if (co && pe) return `${co} — ${pe}`;
  return co || pe || '';
}

/**
 * One option per contact, with labels guaranteed DISTINCT.
 *
 * The id is a tiebreaker rather than decoration. Real data contains rows with no company and no
 * name at all — several of them — and they would otherwise render as identical blank entries, which
 * is precisely the ambiguity this picker exists to remove. So the ClientID is appended only where a
 * label would otherwise repeat, keeping the common case readable.
 */
export function contactOptions(clients: Client[]): SelectOption[] {
  const counts = new Map<string, number>();
  clients.forEach((c) => {
    const l = baseLabel(c);
    counts.set(l, (counts.get(l) ?? 0) + 1);
  });

  return clients.map((c) => {
    const label = baseLabel(c);
    const ambiguous = !label || (counts.get(label) ?? 0) > 1;
    return {
      value: c.ClientID,
      label: ambiguous ? `${label ? `${label} ` : ''}(${c.ClientID})` : label,
    };
  });
}

/**
 * The company to denormalise onto `bids.client` when a contact is picked.
 *
 * Returns the picked contact's company VERBATIM — including empty. An earlier version fell back to
 * the previously-typed company when the new contact's was blank, which left the bid displaying and
 * filtering under a company its contact does not belong to.
 */
export function companyOf(clients: Client[], clientId: string): string {
  if (!clientId) return '';
  return company(clients.find((c) => c.ClientID === clientId) ?? ({} as Client));
}
